import { FC, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { RefreshCcw } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Text } from "@radix-ui/themes";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { atlasRoutes } from "@/app/router/routes";
import { Button } from "@/components/button";
import { StatCard } from "@/components/metrics/stat-card";
import { Table } from "@/components/table/table";
import { TextFieldRow } from "@/components/table/text-field-row";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import {
  AudioRecordingQualityPlatformKind,
  AudioRecordingQualityData,
  AudioRecordingState,
  fetchAudioRecordingQualityMetricsInternal,
} from "@/pages/audio-recordings/shared/api";

const PLATFORM_KINDS: { value: AudioRecordingQualityPlatformKind; label: string }[] = [
  { value: "ALL", label: "All platforms" },
  { value: "web", label: "Web" },
  { value: "macos", label: "macOS" },
  { value: "windows", label: "Windows" },
  { value: "ios", label: "iOS" },
];

const DEFAULT_PLATFORM_KIND: AudioRecordingQualityPlatformKind = "ALL";

const EXCLUDED_RECORDING_IDS = new Set([
  "c0edab88-439c-4026-8a98-66029424c872",
  "2f216ace-cb47-45ee-b9a8-ceaeb0ba4d82",
  "2ab603be-4b36-464a-98c6-dc539fd00950",
  "a5952d7e-33ac-4dfc-8be0-b63a3226af18",
  "65aa1060-941d-45d5-9a92-e8eca83570da",
  "2eca5cca-6798-4ca5-9af5-b9347365de14",
  "95387c2f-1a20-484a-85db-c847762e0951",
  "b5008394-e182-48c5-a430-3139300c39f3",
  "1c1da6ec-c16d-40c5-8ee1-9ce4684acf71",
  "b6bc1ddf-cc32-4b15-84a4-c2e77fa30df2",
  "d7e8058e-1081-43f7-925f-d3ff5f47bd78",
  "e8ec1695-8389-4461-a463-55acd95e670e",
  "72493ef0-f997-4c1d-88da-79bc11a1507c",
  "10131793-48d8-48f4-94a1-0b7436c96c17",
  "87d75684-9c6a-4720-b0d3-43d3d9600c91",
  "3e0fb345-1bea-41e8-b9fe-9fe53ebc2041",
  "317704da-6113-4c23-bf48-8d60e2cb0f67",
  "34a2f229-ed55-4933-8264-ac98524d4bdc",
  "7f765eac-afb9-4a8c-bd82-230b6523c475",
  "3f72b313-aaa7-4c1a-a5bb-f9d70e844e80",
  "6af91aac-2fe1-450f-b118-6dbda4cf52a5",
  "7a41639a-4168-442a-a120-9667f7eb5cd6",
  "1f6361ad-60fb-44b9-897f-48d6f702a71c",
  "7712d37b-3535-40ee-a3bd-45f90c853e8f",
  "c15250d6-97c9-4413-a70a-2b12c4faa102",
  "8ea12ca0-cdd7-4a62-85ac-5c7dff602752",
  "6282d4a3-5d71-439c-b7fb-38b098757e09",
  "cc29ee25-3328-43a4-a0dc-4b7a2daf7c7a",
  "935124a8-b4bd-4869-9e8b-3bac266d34a1",
  "7b61da75-3a39-4448-b629-4e41dc9e43a3",
  "e718a369-a79f-44e3-9e27-f13c054fad19",
  "ffb37ac2-e83e-4daa-9825-8b17918aafda",
  "8d901fc9-182f-47a7-bc0b-af41b7c8ee49",
  "1c26df0d-ec5a-45ef-abf5-7014c7bb6092",
  "22294d89-bb8c-4962-9608-8251c81f9370",
  "57751e55-c271-40d8-a9f8-d480c34e7900",
  "4516182f-db7e-420a-bc27-02c351acfdde",
  "ee7a99b7-9e5d-4e02-ae7a-b3f3af8f332a",
  "290bf830-83e8-4d6f-85c5-e9b2cbb1a044",
  "33cb91c0-5a0b-47c0-8007-14ec9c9eb84c",
  "6e40e17d-e285-44af-b85c-b249e4184fb7",
  "31cd47c0-92b1-4930-806b-3cc3f3d43502",
  "fd6abfe0-6055-40c8-9f1e-38ff5df56f6a",
  "e2ef7435-cb29-4af8-a648-0528e06ed6f4",
  "633bab49-8cfe-4d47-aac2-ad0586206b0c",
  "90a8acea-8b52-4b31-ac89-74ab1ab256a4",
  "6b051223-cb55-45af-951d-ccf6d0c8870f",
  "372ce4bc-e878-421a-906a-fa6d519bddbb",
  "c6903da7-c6ee-4aba-8fe7-8ef500008ed0",
  "50a08ec3-27c4-4b31-bfb6-57e5d429ba8b",
  "5d573282-3131-464e-90fc-654036dc2790",
  "cc4fdc0a-6cb4-4341-904f-71011e5b72bc",
  "a11b0989-6ddc-4a0f-a066-9549386f5b7b",
  "5c75c8e6-1aa1-4cac-ae9a-f661c50bdc76",
]);

interface BreakdownEntry {
  reason: string;
  count: number;
}

type StatTone = "default" | "success" | "warning" | "danger";

interface RecordingRow {
  audioRecordingId: string;
  createdAt: string | null;
  state: AudioRecordingState;
  classification: "warning" | "failure" | null;
  platformKind: string | null;
  audioDuration: number | null;
  finalizationDuration: number | null;
  uploadDuration: number | null;
  transformDuration: number | null;
  primaryReason: string;
  warningReasons: string[];
  failureReasons: string[];
  eventCount: number;
}

interface DailyStats {
  date: string;
  dateLabel: string;
  totalCount: number;
  warningCount: number;
  failureCount: number;
  warningRate: number;
  failureRate: number;
}

interface ComputedMetrics {
  totalStarted: number;
  successCount: number;
  failedCount: number;
  pendingCount: number;
  staleCount: number;
  noContentCount: number;
  finishedCount: number;
  warningCount: number;
  failureCount: number;
  warningRate: number | null;
  failureRate: number | null;
  noContentRate: number | null;
  warningBreakdown: Record<string, number>;
  failureBreakdown: Record<string, number>;
  warningIds: string[];
  failureIds: string[];
  noContentIds: string[];
}

const computeMetrics = (
  recordings: Record<string, AudioRecordingQualityData>,
  platformKind: AudioRecordingQualityPlatformKind
): ComputedMetrics => {
  const filteredEntries = Object.entries(recordings).filter(([, data]) => {
    if (platformKind === "ALL") return true;
    return data.metrics.platform_kind === platformKind;
  });

  const metrics: ComputedMetrics = {
    totalStarted: filteredEntries.length,
    successCount: 0,
    failedCount: 0,
    pendingCount: 0,
    staleCount: 0,
    noContentCount: 0,
    finishedCount: 0,
    warningCount: 0,
    failureCount: 0,
    warningRate: null,
    failureRate: null,
    noContentRate: null,
    warningBreakdown: {},
    failureBreakdown: {},
    warningIds: [],
    failureIds: [],
    noContentIds: [],
  };

  for (const [id, data] of filteredEntries) {
    const { state, classification, warning_reasons, failure_reasons } = data.metrics;

    switch (state) {
      case "success":
        metrics.successCount++;
        metrics.finishedCount++;
        break;
      case "failed":
        metrics.failedCount++;
        metrics.finishedCount++;
        break;
      case "pending":
        metrics.pendingCount++;
        break;
      case "stale":
        metrics.staleCount++;
        break;
      case "no_content":
        metrics.noContentCount++;
        metrics.noContentIds.push(id);
        break;
    }

    // Exclude no_content recordings from warning/failure metrics
    if (state !== "no_content") {
      if (classification === "warning") {
        metrics.warningCount++;
        metrics.warningIds.push(id);
      } else if (classification === "failure") {
        metrics.failureCount++;
        metrics.failureIds.push(id);
      }

      for (const reason of warning_reasons) {
        metrics.warningBreakdown[reason] = (metrics.warningBreakdown[reason] || 0) + 1;
      }

      for (const reason of failure_reasons) {
        metrics.failureBreakdown[reason] = (metrics.failureBreakdown[reason] || 0) + 1;
      }
    }
  }

  if (metrics.finishedCount > 0) {
    metrics.warningRate = metrics.warningCount / metrics.finishedCount;
    metrics.failureRate = metrics.failureCount / metrics.finishedCount;
  }

  const terminalCount = metrics.finishedCount + metrics.noContentCount;
  if (terminalCount > 0) {
    metrics.noContentRate = metrics.noContentCount / terminalCount;
  }

  return metrics;
};

const computeLast24hMetrics = (
  recordings: Record<string, AudioRecordingQualityData>,
  platformKind: AudioRecordingQualityPlatformKind
): {
  warningRate: number | null;
  failureRate: number | null;
  warningCount: number;
  failureCount: number;
  finishedCount: number;
} => {
  const now = DateTime.now();
  const cutoff = now.minus({ hours: 24 });

  const filteredEntries = Object.entries(recordings).filter(([, data]) => {
    // Filter by platform
    if (platformKind !== "ALL" && data.metrics.platform_kind !== platformKind) {
      return false;
    }
    // Filter by time - check if the first event is within last 24h
    const firstEvent = data.events[0];
    if (!firstEvent) return false;
    const eventTime = DateTime.fromISO(firstEvent.occurred_at);
    return eventTime.isValid && eventTime >= cutoff;
  });

  let finishedCount = 0;
  let warningCount = 0;
  let failureCount = 0;

  for (const [, data] of filteredEntries) {
    const { state, classification } = data.metrics;

    // Count finished recordings (success or failed, excluding no_content)
    if (state === "success" || state === "failed") {
      finishedCount++;
    }

    // Exclude no_content from warning/failure metrics
    if (state !== "no_content") {
      if (classification === "warning") {
        warningCount++;
      } else if (classification === "failure") {
        failureCount++;
      }
    }
  }

  return {
    warningRate: finishedCount > 0 ? warningCount / finishedCount : null,
    failureRate: finishedCount > 0 ? failureCount / finishedCount : null,
    warningCount,
    failureCount,
    finishedCount,
  };
};

const computeDailyStats = (
  recordings: Record<string, AudioRecordingQualityData>,
  platformKind: AudioRecordingQualityPlatformKind
): DailyStats[] => {
  // Filter by platform
  const filteredEntries = Object.entries(recordings).filter(([, data]) => {
    if (platformKind === "ALL") return true;
    return data.metrics.platform_kind === platformKind;
  });

  // Group recordings by day based on their first event (record_status_start)
  const buckets = new Map<string, { total: number; warnings: number; failures: number }>();

  // Initialize last 7 days
  for (let i = 6; i >= 0; i--) {
    const date = DateTime.now().minus({ days: i }).toISODate();
    if (date) {
      buckets.set(date, { total: 0, warnings: 0, failures: 0 });
    }
  }

  for (const [, data] of filteredEntries) {
    // Find the first event to determine the recording's date
    const firstEvent = data.events[0];
    if (!firstEvent) continue;

    const eventDate = DateTime.fromISO(firstEvent.occurred_at);
    if (!eventDate.isValid) continue;

    const dateKey = eventDate.toISODate();
    if (!dateKey || !buckets.has(dateKey)) continue;

    const bucket = buckets.get(dateKey)!;
    bucket.total++;

    if (data.metrics.classification === "failure") {
      bucket.failures++;
    } else if (data.metrics.classification === "warning") {
      bucket.warnings++;
    }
  }

  // Convert to array with rates
  const stats: DailyStats[] = [];
  for (const [date, bucket] of buckets) {
    const dt = DateTime.fromISO(date);
    stats.push({
      date,
      dateLabel: dt.toFormat("MMM d"),
      totalCount: bucket.total,
      warningCount: bucket.warnings,
      failureCount: bucket.failures,
      warningRate: bucket.total > 0 ? (bucket.warnings / bucket.total) * 100 : 0,
      failureRate: bucket.total > 0 ? (bucket.failures / bucket.total) * 100 : 0,
    });
  }

  return stats.sort((a, b) => a.date.localeCompare(b.date));
};

const toSortedBreakdown = (breakdown: Record<string, number>): BreakdownEntry[] => {
  return Object.entries(breakdown)
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count);
};

const formatPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "N/A";
  }

  return `${(value * 100).toFixed(1)}%`;
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "N/A";
  }

  return value.toLocaleString();
};

const formatDuration = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return "N/A";
  }

  const rounded = Math.round(value);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const seconds = rounded % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }

  return `${seconds}s`;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) {
    return "N/A";
  }

  return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED);
};

const statusToneForValue = (status: string): { background: string; color: string } => {
  const normalized = status.toLowerCase();

  if (normalized.includes("fail") || normalized.includes("error")) {
    return { background: "#fee2e2", color: "#991b1b" };
  }

  if (normalized.includes("warn")) {
    return { background: "#fef3c7", color: "#92400e" };
  }

  if (normalized.includes("no_content")) {
    return { background: "#fef3c7", color: "#92400e" };
  }

  if (normalized.includes("success") || normalized.includes("complete")) {
    return { background: "#dcfce7", color: "#166534" };
  }

  if (normalized.includes("pending")) {
    return { background: "#e0f2fe", color: "#075985" };
  }

  if (normalized.includes("stale")) {
    return { background: "#f3f4f6", color: "#6b7280" };
  }

  return { background: "#f3f4f6", color: "#374151" };
};

const toneForNonZero = (value: number | null | undefined, tone: StatTone): StatTone => {
  if (!value || value <= 0) {
    return "default";
  }

  return tone;
};

const getPlatformLabel = (platformKind: AudioRecordingQualityPlatformKind): string =>
  PLATFORM_KINDS.find(option => option.value === platformKind)?.label ?? "All platforms";

const useSelectedPlatformKind = (): [
  AudioRecordingQualityPlatformKind,
  (value: AudioRecordingQualityPlatformKind) => void,
] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const platformParam = searchParams.get("platform");

  const selectedPlatform = useMemo(() => {
    if (!platformParam) {
      return DEFAULT_PLATFORM_KIND;
    }

    const match = PLATFORM_KINDS.find(option => option.value === platformParam);
    return match ? match.value : DEFAULT_PLATFORM_KIND;
  }, [platformParam]);

  const setPlatformKind = (value: AudioRecordingQualityPlatformKind) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value === DEFAULT_PLATFORM_KIND) {
      nextParams.delete("platform");
    } else {
      nextParams.set("platform", value);
    }
    setSearchParams(nextParams);
  };

  return [selectedPlatform, setPlatformKind];
};

export const AudioRecordingsQualityMetricsPage: FC = () => {
  const [platformKind, setPlatformKind] = useSelectedPlatformKind();
  const [lastUpdated, setLastUpdated] = useState<DateTime | null>(null);
  const [visibleRowCount, setVisibleRowCount] = useState(100);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(true);
  const [showPending, setShowPending] = useState(false);
  const [showStale, setShowStale] = useState(false);
  const [showNoContent, setShowNoContent] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["audio-recordings-quality-metrics"],
    queryFn: () => fetchAudioRecordingQualityMetricsInternal(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  useEffect(() => {
    if (data) {
      setLastUpdated(DateTime.now());
    }
  }, [data]);

  useEffect(() => {
    setVisibleRowCount(100);
  }, [data, platformKind]);

  const allRecordings = data?.recordings ?? {};

  const recordings = useMemo(() => {
    const filtered: Record<string, AudioRecordingQualityData> = {};
    for (const [id, recordingData] of Object.entries(allRecordings)) {
      if (!EXCLUDED_RECORDING_IDS.has(id)) {
        filtered[id] = recordingData;
      }
    }
    return filtered;
  }, [allRecordings]);

  const computedMetrics = useMemo(
    () => computeMetrics(recordings, platformKind),
    [recordings, platformKind]
  );

  const warningBreakdown = useMemo(
    () => toSortedBreakdown(computedMetrics.warningBreakdown),
    [computedMetrics.warningBreakdown]
  );

  const failureBreakdown = useMemo(
    () => toSortedBreakdown(computedMetrics.failureBreakdown),
    [computedMetrics.failureBreakdown]
  );

  const dailyStats = useMemo(
    () => computeDailyStats(recordings, platformKind),
    [recordings, platformKind]
  );

  const last24hMetrics = useMemo(
    () => computeLast24hMetrics(recordings, platformKind),
    [recordings, platformKind]
  );

  const recordingRows = useMemo<RecordingRow[]>(() => {
    const filteredEntries = Object.entries(recordings).filter(([, recordingData]) => {
      if (platformKind === "ALL") return true;
      return recordingData.metrics.platform_kind === platformKind;
    });

    return filteredEntries
      .map(([id, recordingData]) => {
        const { metrics, events } = recordingData;
        const primaryReason = metrics.failure_reasons[0] || metrics.warning_reasons[0] || "None";
        const createdAt = events[0]?.occurred_at ?? null;

        return {
          audioRecordingId: id,
          createdAt,
          state: metrics.state,
          classification: metrics.classification,
          platformKind: metrics.platform_kind,
          audioDuration: metrics.audio_duration_seconds,
          finalizationDuration: metrics.finalization_duration,
          uploadDuration: metrics.upload_duration,
          transformDuration: metrics.transform_duration,
          primaryReason,
          warningReasons: metrics.warning_reasons,
          failureReasons: metrics.failure_reasons,
          eventCount: events.length,
        };
      })
      .filter(row => {
        if (!showSuccess && row.state === "success") return false;
        if (!showFailed && row.state === "failed") return false;
        if (!showPending && row.state === "pending") return false;
        if (!showStale && row.state === "stale") return false;
        if (!showNoContent && row.state === "no_content") return false;
        return true;
      })
      .sort((a, b) => {
        // Sort by creation time (newest first)
        if (a.createdAt && b.createdAt) {
          return b.createdAt.localeCompare(a.createdAt);
        }
        if (a.createdAt) return -1;
        if (b.createdAt) return 1;
        return a.audioRecordingId.localeCompare(b.audioRecordingId);
      });
  }, [recordings, platformKind, showSuccess, showFailed, showPending, showStale, showNoContent]);

  const recordingColumns = useMemo<ColumnDef<RecordingRow>[]>(
    () => [
      {
        header: "Recording ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.audioRecordingId} />,
      },
      {
        header: "Created",
        cell: ({ row }) => <TextFieldRow value={formatDateTime(row.original.createdAt)} />,
      },
      {
        header: "State",
        cell: ({ row }) => (
          <StatusBadge status={row.original.state}>{row.original.state}</StatusBadge>
        ),
      },
      {
        header: "Classification",
        cell: ({ row }) =>
          row.original.classification ? (
            <StatusBadge status={row.original.classification}>
              {row.original.classification}
            </StatusBadge>
          ) : (
            <TextFieldRow value="-" />
          ),
      },
      {
        header: "Platform",
        cell: ({ row }) => <TextFieldRow value={row.original.platformKind ?? "N/A"} />,
      },
      {
        header: "Duration",
        cell: ({ row }) => <TextFieldRow value={formatDuration(row.original.audioDuration)} />,
      },
      {
        header: "Finalization",
        cell: ({ row }) => (
          <TextFieldRow value={formatDuration(row.original.finalizationDuration)} />
        ),
      },
      {
        header: "Upload",
        cell: ({ row }) => <TextFieldRow value={formatDuration(row.original.uploadDuration)} />,
      },
      {
        header: "Transform",
        cell: ({ row }) => <TextFieldRow value={formatDuration(row.original.transformDuration)} />,
      },
      {
        header: "Primary reason",
        cell: ({ row }) => <TextFieldRow value={row.original.primaryReason} />,
      },
      {
        header: "Events",
        cell: ({ row }) => <TextFieldRow value={String(row.original.eventCount)} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <ViewLink
            to={
              atlasRoutes.audioRecordingsView({
                params: { audioRecordingId: row.original.audioRecordingId },
              }).path
            }
          >
            View
          </ViewLink>
        ),
      },
    ],
    []
  );

  const errorMessage = error instanceof Error ? error.message : "Failed to load quality metrics.";

  return (
    <PageContainer>
      <HeaderRow>
        <div>
          <PageTitle>Audio Recording Quality</PageTitle>
          <Subtitle>
            Track warnings, failures, and no-content uploads for the last 7 days
            {platformKind !== "ALL" && ` on ${getPlatformLabel(platformKind)}`}.
          </Subtitle>
          <HeuristicInfo>
            <strong>Finalization threshold heuristic:</strong>
            <ul>
              <li>Formula: 60s + (audio_duration / 20)</li>
              <li>Warning at 50% of threshold, failure at 100%</li>
              <li>5 min recording: warn at 37s, fail at 75s</li>
              <li>30 min recording: warn at 75s, fail at 2.5 min</li>
            </ul>
          </HeuristicInfo>
        </div>
        <Actions>
          <ControlRow>
            <label htmlFor="platform-select">Platform</label>
            <Select
              id="platform-select"
              value={platformKind}
              onChange={event =>
                setPlatformKind(event.target.value as AudioRecordingQualityPlatformKind)
              }
            >
              {PLATFORM_KINDS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </ControlRow>
          <LastUpdated>
            Last updated:{" "}
            {lastUpdated ? lastUpdated.toLocaleString(DateTime.TIME_WITH_SECONDS) : "N/A"}
          </LastUpdated>
          <Button variant="outline" size="1" onClick={() => refetch()} disabled={isFetching}>
            <ButtonContent>
              <RefreshCcw size={14} />
              <span>{isFetching ? "Refreshing" : "Refresh"}</span>
            </ButtonContent>
          </Button>
        </Actions>
      </HeaderRow>

      {data && (
        <WindowSummary>
          <WindowItem>
            <WindowLabel>Window start</WindowLabel>
            <WindowValue>
              {formatDateTime(data.window_start)} ({formatNumber(computedMetrics.totalStarted)}{" "}
              recordings)
            </WindowValue>
          </WindowItem>
          <WindowItem>
            <WindowLabel>Window end</WindowLabel>
            <WindowValue>{formatDateTime(data.window_end)}</WindowValue>
          </WindowItem>
        </WindowSummary>
      )}

      {isLoading && <StateMessage>Loading quality metrics...</StateMessage>}
      {isError && <StateMessage $tone="error">{errorMessage}</StateMessage>}

      {!isLoading && !data && !isError && (
        <StateMessage>No quality metrics available.</StateMessage>
      )}

      {data && (
        <>
          <StatsGrid>
            <StatCard
              label="Warning rate (last 24h)"
              value={formatPercent(last24hMetrics.warningRate)}
              tone={toneForNonZero(last24hMetrics.warningRate, "warning")}
              description={`${formatNumber(last24hMetrics.warningCount)} of ${formatNumber(last24hMetrics.finishedCount)} sessions`}
            />
            <StatCard
              label="Failure rate (last 24h)"
              value={formatPercent(last24hMetrics.failureRate)}
              tone={toneForNonZero(last24hMetrics.failureRate, "danger")}
              description={`${formatNumber(last24hMetrics.failureCount)} of ${formatNumber(last24hMetrics.finishedCount)} sessions`}
            />
          </StatsGrid>

          <ChartCard>
            <SectionTitle>Quality Trend (7 days)</SectionTitle>
            <Text size="1" color="gray">
              Daily warning and failure rates over the past week.
            </Text>
            <ChartContainer>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={dailyStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-4)" />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 12, fill: "var(--gray-11)" }}
                    tickLine={{ stroke: "var(--gray-6)" }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tickFormatter={(v: number) => `${v}%`}
                    tick={{ fontSize: 12, fill: "var(--gray-11)" }}
                    tickLine={{ stroke: "var(--gray-6)" }}
                  />
                  <Tooltip
                    formatter={(value: number) => [`${value.toFixed(1)}%`]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid var(--gray-4)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(label: string) => `${label}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                  <Area
                    type="monotone"
                    dataKey="failureRate"
                    stackId="1"
                    stroke="#dc2626"
                    fill="#dc2626"
                    fillOpacity={0.6}
                    name="Failure %"
                  />
                  <Area
                    type="monotone"
                    dataKey="warningRate"
                    stackId="1"
                    stroke="#f59e0b"
                    fill="#f59e0b"
                    fillOpacity={0.6}
                    name="Warning %"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
            <ChartLegend>
              {dailyStats.map(day => (
                <ChartLegendItem key={day.date}>
                  <span>{day.dateLabel}</span>
                  <span>{day.totalCount} recordings</span>
                </ChartLegendItem>
              ))}
            </ChartLegend>
          </ChartCard>

          <BreakdownGrid>
            <TableCard>
              <SectionTitle>Warning breakdown</SectionTitle>
              {warningBreakdown.length === 0 ? (
                <EmptyText>No warning breakdown entries.</EmptyText>
              ) : (
                <TableContainer>
                  <MetricsTable>
                    <thead>
                      <tr>
                        <Th>Reason</Th>
                        <Th>Count</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {warningBreakdown.map(entry => (
                        <tr key={`warning-${entry.reason}`}>
                          <Td>{entry.reason}</Td>
                          <Td>{formatNumber(entry.count)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </MetricsTable>
                </TableContainer>
              )}
            </TableCard>

            <TableCard>
              <SectionTitle>Failure breakdown</SectionTitle>
              {failureBreakdown.length === 0 ? (
                <EmptyText>No failure breakdown entries.</EmptyText>
              ) : (
                <TableContainer>
                  <MetricsTable>
                    <thead>
                      <tr>
                        <Th>Reason</Th>
                        <Th>Count</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {failureBreakdown.map(entry => (
                        <tr key={`failure-${entry.reason}`}>
                          <Td>{entry.reason}</Td>
                          <Td>{formatNumber(entry.count)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </MetricsTable>
                </TableContainer>
              )}
            </TableCard>
          </BreakdownGrid>

          <TableCard>
            <TableHeader>
              <SectionTitle>Recordings ({recordingRows.length})</SectionTitle>
              <FilterCheckboxes>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={showSuccess}
                    onChange={e => setShowSuccess(e.target.checked)}
                  />
                  Success
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={showFailed}
                    onChange={e => setShowFailed(e.target.checked)}
                  />
                  Failed
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={showPending}
                    onChange={e => setShowPending(e.target.checked)}
                  />
                  Pending
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={showStale}
                    onChange={e => setShowStale(e.target.checked)}
                  />
                  Stale
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={showNoContent}
                    onChange={e => setShowNoContent(e.target.checked)}
                  />
                  No content
                </CheckboxLabel>
              </FilterCheckboxes>
            </TableHeader>
            <Text size="1" color="gray">
              All recordings in the time window with their computed metrics.
              {recordingRows.length > visibleRowCount &&
                ` Showing ${visibleRowCount} of ${recordingRows.length}.`}
            </Text>
            {recordingRows.length === 0 ? (
              <EmptyText>No recordings found.</EmptyText>
            ) : (
              <>
                <Table
                  data={recordingRows.slice(0, visibleRowCount)}
                  columns={recordingColumns}
                  pageSize={visibleRowCount}
                />
                {recordingRows.length > visibleRowCount && (
                  <LoadMoreButton
                    variant="outline"
                    size="2"
                    onClick={() => setVisibleRowCount(prev => prev + 100)}
                  >
                    Load more ({recordingRows.length - visibleRowCount} remaining)
                  </LoadMoreButton>
                )}
              </>
            )}
          </TableCard>

          <IdGrid>
            <IdListCard>
              <SectionTitle>Warning IDs ({computedMetrics.warningIds.length})</SectionTitle>
              <IdList ids={computedMetrics.warningIds} />
            </IdListCard>
            <IdListCard>
              <SectionTitle>Failure IDs ({computedMetrics.failureIds.length})</SectionTitle>
              <IdList ids={computedMetrics.failureIds} />
            </IdListCard>
            <IdListCard>
              <SectionTitle>No-content IDs ({computedMetrics.noContentIds.length})</SectionTitle>
              <IdList ids={computedMetrics.noContentIds} />
            </IdListCard>
          </IdGrid>
        </>
      )}
    </PageContainer>
  );
};

const IdList: FC<{ ids: string[] }> = ({ ids }) => {
  if (ids.length === 0) {
    return <EmptyText>No recordings.</EmptyText>;
  }

  return (
    <IdListContainer>
      <ul>
        {ids.map(id => (
          <li key={id}>
            <Link to={atlasRoutes.audioRecordingsView({ params: { audioRecordingId: id } }).path}>
              <MonoText>{id}</MonoText>
            </Link>
          </li>
        ))}
      </ul>
    </IdListContainer>
  );
};

const PageContainer = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
`;

const Subtitle = styled.p`
  margin: 4px 0 0;
  color: var(--gray-11);
  font-size: 14px;
`;

const HeuristicInfo = styled.div`
  margin: 8px 0 0;
  color: var(--gray-11);
  font-size: 12px;

  ul {
    margin: 4px 0 0;
    padding-left: 20px;
  }

  li {
    margin: 2px 0;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--gray-11);

  label {
    font-weight: 600;
  }
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--gray-5);
  background: white;
  font-size: 12px;
  color: var(--gray-12);
`;

const LastUpdated = styled.span`
  color: var(--gray-11);
  font-size: 12px;
  white-space: nowrap;
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const WindowSummary = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px 20px;
  border: 1px solid var(--gray-4);
  border-radius: 12px;
  background: white;
`;

const WindowItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WindowLabel = styled.span`
  font-size: 12px;
  color: var(--gray-11);
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const WindowValue = styled.span`
  font-size: 14px;
  color: var(--gray-12);
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`;

const ChartCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border: 1px solid var(--gray-4);
  border-radius: 12px;
  background: white;
`;

const ChartContainer = styled.div`
  width: 100%;
  min-height: 300px;
`;

const ChartLegend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid var(--gray-3);
`;

const ChartLegendItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
  color: var(--gray-11);

  span:first-of-type {
    font-weight: 600;
    color: var(--gray-12);
  }
`;

const TableCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterCheckboxes = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--gray-11);
  cursor: pointer;

  input[type="checkbox"] {
    cursor: pointer;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const MetricsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    text-align: left;
    padding: 8px;
    border-bottom: 1px solid var(--gray-4);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: var(--gray-2);
  }
`;

const Th = styled.th`
  font-weight: 600;
  color: var(--gray-12);
  background: var(--gray-1);
`;

const Td = styled.td`
  color: var(--gray-11);
`;

const MonoText = styled.span`
  font-family: monospace;
  font-size: 12px;
`;

const ViewLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--gray-12);
  background: var(--gray-3);
  border-radius: 4px;
  text-decoration: none;

  &:hover {
    background: var(--gray-4);
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${({ status }) => statusToneForValue(status).background};
  color: ${({ status }) => statusToneForValue(status).color};
`;

const BreakdownGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
`;

const IdGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
`;

const IdListCard = styled.div`
  border: 1px solid var(--gray-4);
  border-radius: 12px;
  padding: 20px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IdListContainer = styled.div`
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid var(--gray-3);
  border-radius: 8px;
  padding: 12px;
  background: var(--gray-1);

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 8px;
  }

  a {
    color: var(--gray-12);
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const EmptyText = styled(Text)`
  color: var(--gray-11);
`;

const LoadMoreButton = styled(Button)`
  align-self: center;
  margin-top: 8px;
`;

const StateMessage = styled.div<{ $tone?: "error" | "default" }>`
  padding: 24px;
  border-radius: 12px;
  background: ${({ $tone }) => ($tone === "error" ? "var(--red-3)" : "var(--gray-1)")};
  color: ${({ $tone }) => ($tone === "error" ? "var(--red-11)" : "var(--gray-11)")};
  border: 1px solid ${({ $tone }) => ($tone === "error" ? "var(--red-5)" : "var(--gray-4)")};
`;
